import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { MessageSquare, Star, TrendingUp, ThumbsUp, ThumbsDown, Check, X, Archive, Trash2, MoreHorizontal, Clock, TrendingDown, Heart } from "lucide-react";
import { format } from "date-fns";
import { useFeedbackManagement } from "@/hooks/useFeedbackManagement";
import { SessionRecord } from "@/types/session";

interface ClientFeedbackProps {
  feedback: any;
  clientName: string;
  sessionId: string;
  feedbackStatus?: string;
  onFeedbackUpdate?: () => void;
}

export function ClientFeedbackDisplay({ feedback, clientName, sessionId, feedbackStatus = 'unread', onFeedbackUpdate }: ClientFeedbackProps) {
  const { markAsRead, markAsUnread, deleteFeedback, archiveFeedback, loading } = useFeedbackManagement();

  if (!feedback || feedbackStatus === 'deleted') {
    return null;
  }

  const handleMarkAsRead = async () => {
    await markAsRead(sessionId);
    onFeedbackUpdate?.();
  };

  const handleMarkAsUnread = async () => {
    await markAsUnread(sessionId);
    onFeedbackUpdate?.();
  };

  const handleDelete = async () => {
    await deleteFeedback(sessionId);
    onFeedbackUpdate?.();
  };

  const handleArchive = async () => {
    await archiveFeedback(sessionId);
    onFeedbackUpdate?.();
  };

  const isUnread = feedbackStatus === 'unread';
  const isArchived = feedbackStatus === 'archived';

  // Handle simple feedback with feedback property (like {feedback: "text", submitted_at: "date"})
  if (typeof feedback === 'object' && feedback.feedback && typeof feedback.feedback === 'string') {
    return (
      <Card className={`mt-3 ${isArchived ? 'opacity-75' : ''} ${isUnread ? 'bg-blue-50/50 dark:bg-blue-950/30 border-blue-200/50 dark:border-blue-800/50' : 'bg-green-50/50 dark:bg-green-950/30 border-green-200/50 dark:border-green-800/50'}`}>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MessageSquare className={`h-4 w-4 ${isUnread ? 'text-blue-600 dark:text-blue-400' : 'text-green-600 dark:text-green-400'}`} />
              <CardTitle className={`text-sm ${isUnread ? 'text-blue-800 dark:text-blue-200' : 'text-green-800 dark:text-green-200'}`}>Client Feedback</CardTitle>
              <Badge variant="outline" className={`text-xs ${isUnread ? 'text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-600' : 'text-green-700 dark:text-green-300 border-green-300 dark:border-green-600'}`}>
                {clientName}
              </Badge>
              {isUnread && (
                <Badge variant="default" className="text-xs bg-blue-600 text-white">
                  New
                </Badge>
              )}
              {isArchived && (
                <Badge variant="secondary" className="text-xs">
                  Archived
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2">
              {feedback.submitted_at && (
                <CardDescription className="text-xs">
                  {format(new Date(feedback.submitted_at), 'MMM d, h:mm a')}
                </CardDescription>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0" disabled={loading}>
                    <MoreHorizontal className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {isUnread ? (
                    <DropdownMenuItem onClick={handleMarkAsRead}>
                      <Check className="h-3 w-3 mr-2" />
                      Mark as Read
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem onClick={handleMarkAsUnread}>
                      <X className="h-3 w-3 mr-2" />
                      Mark as Unread
                    </DropdownMenuItem>
                  )}
                  {!isArchived && (
                    <DropdownMenuItem onClick={handleArchive}>
                      <Archive className="h-3 w-3 mr-2" />
                      Archive
                    </DropdownMenuItem>
                  )}
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                        <Trash2 className="h-3 w-3 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Feedback</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to delete this feedback? This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <p className={`text-sm ${isUnread ? 'text-blue-700 dark:text-blue-300' : 'text-green-700 dark:text-green-300'}`}>{feedback.feedback}</p>
        </CardContent>
      </Card>
    );
  }

  // Handle other feedback formats (structured feedback, simple string, etc.)
  return (
    <Card className="mt-3 bg-green-50/50 dark:bg-green-950/30 border-green-200/50 dark:border-green-800/50">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-green-600 dark:text-green-400" />
          <CardTitle className="text-sm text-green-800 dark:text-green-200">Client Feedback</CardTitle>
          <Badge variant="outline" className="text-xs text-green-700 dark:text-green-300 border-green-300 dark:border-green-600">
            {clientName}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <p className="text-sm text-green-700 dark:text-green-300">
          {typeof feedback === 'string' ? feedback : JSON.stringify(feedback)}
        </p>
      </CardContent>
    </Card>
  );
}